/**
 * Duel Resolution — Resolves expired active duels client-side.
 *
 * Called from DuelsScreen on load/refresh. For each expired active duel:
 *   1. Fetch strength_events in the duel window for both players
 *   2. Sum total volume (weight × reps) as the score
 *   3. Determine winner, update duel row, call onDuelCompleted
 */

import { supabase } from './supabase';
import { onDuelCompleted } from './duelEloHook';
import { createLogger } from './logger';

const log = createLogger('DUEL_RESOLUTION');

export async function resolveExpiredDuels(profileId: string): Promise<number> {
  let resolved = 0;

  try {
    // Fetch active duels that have expired
    const { data: expiredDuels, error } = await supabase
      .from('duels')
      .select('*')
      .eq('status', 'active')
      .or(`challenger_id.eq.${profileId},challenged_id.eq.${profileId}`)
      .not('end_at', 'is', null)
      .lte('end_at', new Date().toISOString());

    if (error) {
      log.error('Failed to fetch expired duels:', error);
      return 0;
    }

    if (!expiredDuels || expiredDuels.length === 0) return 0;

    log.info(`Found ${expiredDuels.length} expired duels to resolve`);

    for (const duel of expiredDuels) {
      try {
        await resolveDuel(duel);
        resolved++;
      } catch (err) {
        log.error(`Failed to resolve duel ${duel.id}:`, err);
      }
    }
  } catch (err) {
    log.error('resolveExpiredDuels failed:', err);
  }

  return resolved;
}

async function resolveDuel(duel: any): Promise<void> {
  const { id, challenger_id, challenged_id, start_at, end_at, lift_key } = duel;

  if (!start_at || !end_at) {
    log.warn(`Duel ${id} missing start_at/end_at, marking expired`);
    await supabase.from('duels').update({ status: 'expired' }).eq('id', id);
    return;
  }

  // Fetch strength events in the duel window for both players
  const [challengerScore, challengedScore] = await Promise.all([
    getPlayerScore(challenger_id, start_at, end_at, lift_key),
    getPlayerScore(challenged_id, start_at, end_at, lift_key),
  ]);

  let winnerId: string | null = null;
  let loserId: string | null = null;
  let status = 'completed';

  if (challengerScore > challengedScore) {
    winnerId = challenger_id;
    loserId = challenged_id;
  } else if (challengedScore > challengerScore) {
    winnerId = challenged_id;
    loserId = challenger_id;
  }
  // else: draw — winnerId stays null

  // Update duel row
  const { error: updateError } = await supabase
    .from('duels')
    .update({
      status,
      winner_user_id: winnerId,
      challenger_score: Math.round(challengerScore),
      challenged_score: Math.round(challengedScore),
      resolved_at: new Date().toISOString(),
    })
    .eq('id', id)
    .eq('status', 'active'); // optimistic lock

  if (updateError) {
    log.error(`Failed to update duel ${id}:`, updateError);
    return;
  }

  log.info(`Duel ${id} resolved: winner=${winnerId ?? 'draw'}, scores=${challengerScore}/${challengedScore}`);

  // Fire Elo + notifications
  await onDuelCompleted(winnerId, loserId, id, challenger_id, challenged_id);
}

async function getPlayerScore(
  profileId: string,
  startAt: string,
  endAt: string,
  liftKey?: string | null,
): Promise<number> {
  let query = supabase
    .from('strength_events')
    .select('weight, reps, weight_unit')
    .eq('profile_id', profileId)
    .gte('performed_at', startAt)
    .lte('performed_at', endAt);

  if (liftKey) {
    query = query.eq('lift_key', liftKey);
  }

  const { data, error } = await query;

  if (error || !data) return 0;

  // Total volume in lbs
  return data.reduce((sum, ev) => {
    const weightLbs = ev.weight_unit === 'kg' ? ev.weight * 2.20462 : ev.weight;
    return sum + weightLbs * (ev.reps || 1);
  }, 0);
}
